import type { Reactive } from 'vue';

export interface UseModalStateReturn<T> {
    isOpen: boolean;
    open: () => Promise<T | undefined>;
    confirm: (result: T) => void;
    cancel: () => void;
}

export function useModalState<T>(): Reactive<UseModalStateReturn<T>> {
    let resolveFn: ((value: T | undefined) => void) | null = null;

    const state = reactive({
        isOpen: false,

        open(): Promise<T | undefined> {
            state.isOpen = true;

            return new Promise<T | undefined>(resolve => {
                resolveFn = resolve;
            });
        },

        confirm(result: T): void {
            if (resolveFn) {
                resolveFn(result);
                resolveFn = null;
            }

            state.isOpen = false;
        },

        cancel(): void {
            if (resolveFn) {
                resolveFn(undefined);
                resolveFn = null;
            }

            state.isOpen = false;
        },
    });

    // Manage automatic closings (e.g. internal click)
    watch(
        () => state.isOpen,
        openState => {
            if (!openState && resolveFn) {
                // If the modal closes without confirmation
                resolveFn(undefined);
                resolveFn = null;
            }
        }
    );

    return state;
}
